import React, { useEffect, useState } from "react";
import {
  Dialog,
  Box,
  Typography,
  Button,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { LoadingIndicator } from "./assets";

export default function RewardRevealPopup({
  open,
  onClose,
  imageUrl,
  title,
  imageReady,
  loadingTitle = "🎁 New Reward Unlocked!",
  loadingText = "Getting reward ready...",
  allowCloseWhileLoading = false,
}) {
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (open && imageReady) {
      setRevealed(false);
      const timer = setTimeout(() => setRevealed(true), 1200);
      return () => clearTimeout(timer);
    }

    setRevealed(false);
  }, [imageReady, open]);

  const handleReveal = () => setRevealed(true);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          background: "rgba(0,0,0,0.85)",
          boxShadow: "none",
          border: "none",
          overflow: "hidden",
        },
      }}
      fullScreen
    >
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        height="100%"
        textAlign="center"
        color="#fff"
      >
        <Typography variant="h4" sx={{ mb: 4, fontWeight: "bold" }}>
          {imageReady ? "🎁 New Reward Unlocked!" : loadingTitle}
        </Typography>

        {!imageReady ? (
          <Box sx={{ width: 250, height: 250, display: 'grid', placeItems: 'center' }}>
            <LoadingIndicator text="Getting reward" />
          </Box>
        ) : (
          <AnimatePresence mode="wait">
            {!revealed ? (
              <motion.div
                key="mystery"
                initial={{ rotateY: 0, opacity: 1 }}
                animate={{ rotateY: 180 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1, ease: "easeInOut" }}
                onClick={handleReveal}
                style={{
                  width: 250,
                  height: 250,
                  background: "#111",
                  border: "4px solid #FF2D95",
                  borderRadius: "8px",
                  cursor: "pointer",
                }}
              />
            ) : (
              <motion.img
                key="reward"
                src={imageUrl}
                alt="Reward"
                decoding="sync"
                initial={{ rotateY: 180, opacity: 0 }}
                animate={{ rotateY: 0, opacity: 1 }}
                transition={{ duration: 0.9, ease: "easeOut" }}
                style={{
                  width: 250,
                  height: 250,
                  objectFit: "cover",
                  border: "4px solid #39FF14",
                  borderRadius: "8px",
                  boxShadow: "0 0 20px #FF2D95",
                }}
              />
            )}
          </AnimatePresence>
        )}

        <Typography variant="h5" sx={{ mt: 3, fontWeight: "600" }}>
          {imageReady ? (revealed ? title : 'Tap to Reveal') : loadingText}
        </Typography>

        {!imageReady && allowCloseWhileLoading && (
          <Button
            variant="contained"
            onClick={onClose}
            sx={{
              mt: 4,
              fontWeight: "bold",
              px: 3,
              py: 1,
              background: "secondary.main",
              "&:hover": { background: "secondary.main" },
            }}
          >
            Back to Gallery
          </Button>
        )}

        {revealed && imageReady && (
          <Button
            variant="contained"
            onClick={onClose}
            sx={{
              mt: 4,
              fontWeight: "bold",
              px: 3,
              py: 1,
              background: "secondary.main",
              "&:hover": { background: "secondary.main" },
            }}
          >
            Continue
          </Button>
        )}
      </Box>
    </Dialog>
  );
}
